import * as THREE from "three";
import Line, { getPlaneEquation } from "./Line.js";
import Vertex from "./Vertex.js";
import Face from "./Face.js";
import Edge from "./Edge.js";
import { combineGeometryAttributes, createFaceGeometry, pairs, triples, } from "./geometry.js";
export default class Polyhedron {
    #faces;
    #vertices;
    #oppositeVerticies;
    #edges;
    #edgesByVertex;
    #facesByEdge;
    #counter;
    constructor(data) {
        this.#vertices = new Map();
        this.#oppositeVerticies = new Map();
        this.#edges = new Map();
        this.#faces = new Map();
        this.#edgesByVertex = new Map();
        this.#facesByEdge = new Map();
        if (data.vertices) {
            for (const [id, vertexData] of Object.entries(data.vertices)) {
                this.addVertex(vertexData, id);
            }
        }
        if (data.edges) {
            for (const [id, edgeData] of Object.entries(data.edges)) {
                this.addEdge(edgeData, id);
            }
        }
        if (data.faces) {
            for (const [id, faceData] of Object.entries(data.faces)) {
                const face = new Face(id, faceData, this);
                this.#faces.set(id, face);
                for (const [edge] of face.edges()) {
                    let faces = this.#facesByEdge.get(edge.id);
                    if (!faces) {
                        faces = new Set();
                        this.#facesByEdge.set(edge.id, faces);
                    }
                    faces.add(face);
                }
            }
        }
        this.#counter = this.#edges.size + this.#vertices.size + this.#faces.size;
    }
    getVertex(id) {
        if (!this.#vertices.has(id)) {
            throw new Error(`Vertex ${id} not found`);
        }
        return this.#vertices.get(id);
    }
    addVertex(data, id = this.#getNewId()) {
        const vertex = new Vertex(id, data);
        this.#vertices.set(id, vertex);
        return vertex;
    }
    addOppositeVertex(id) {
        let vertex = this.#oppositeVerticies.get(id);
        if (!vertex) {
            vertex = this.getVertex(id);
            const { x, y, z, w } = vertex;
            const newVertex = this.addVertex([-x, -y, -z, -w]);
            this.#oppositeVerticies.set(id, newVertex);
            this.#oppositeVerticies.set(newVertex.id, vertex);
            return newVertex;
        }
        return vertex;
    }
    getAdjecentEdges(id) {
        const edges = this.#edgesByVertex.get(id);
        if (!edges) {
            return [];
        }
        return Array.from(edges);
    }
    findEdgeByVertices(v1, v2) {
        const edges = this.#edgesByVertex.get(v1);
        if (!edges) {
            return [undefined, 0];
        }
        for (const edge of edges) {
            const sign = edge.getVertexSign(v2);
            if (sign !== 0) {
                return [edge, sign];
            }
        }
        return [undefined, 0];
    }
    findFaceByEdges(edges) {
        const faces = new Set(this.#faces.values());
        for (const [edgeId] of edges.entries()) {
            const adjecentFaces = new Set(this.getAdjecentFaces(edgeId));
            for (const face of faces) {
                if (!adjecentFaces.has(face)) {
                    faces.delete(face);
                }
            }
        }
        for (const face of faces) {
            if (face.hasTheSameEdges(edges)) {
                return face;
            }
        }
        return undefined;
    }
    getEdge(id) {
        if (!this.#edges.has(id)) {
            throw new Error(`Edge ${id} not found`);
        }
        return this.#edges.get(id);
    }
    getFace(id) {
        if (!this.#faces.has(id)) {
            throw new Error(`Face ${id} not found`);
        }
        return this.#faces.get(id);
    }
    addEdge(data, id = this.#getNewId()) {
        const edge = new Edge(id, data, this);
        this.#edges.set(id, edge);
        for (const vertex of edge.vertices) {
            let edges = this.#edgesByVertex.get(vertex.id);
            if (!edges) {
                edges = new Set();
                this.#edgesByVertex.set(vertex.id, edges);
            }
            edges.add(edge);
        }
        return edge;
    }
    addFace(vertices) {
        const edges = new Map();
        const plane = new THREE.Vector4();
        for (const [u, v, w] of triples(vertices)) {
            // If all points are coplanar (as they should), we are essentially
            // adding parallel vectors.
            plane.add(getPlaneEquation(u, v, w));
        }
        plane.normalize();
        for (const [u, v] of pairs(vertices)) {
            const line = Line.fromPoints(u, v);
            const [edge, sign] = this.getOrAddEdge(u.id, v.id, line);
            edges.set(edge.id, sign);
        }
        return this.#addFace({
            edges: Object.fromEntries(edges.entries()),
            plane: plane.toArray(),
        });
    }
    getMinEdgeLength() {
        let min = Infinity;
        for (const edge of this.#edges.values()) {
            const length = edge.length;
            if (length < min) {
                min = length;
            }
        }
        return min;
    }
    getOrAddEdge(v1, v2, line, color) {
        const [edge, sign] = this.findEdgeByVertices(v1, v2);
        if (edge && edge.isContainedInLine(line)) {
            return [edge, sign];
        }
        return [this.addEdge({ vertices: [v1, v2], line: line.toArray(), color }), 1];
    }
    getAdjecentFaces(edgeId) {
        const faces = this.#facesByEdge.get(edgeId);
        if (!faces) {
            return [];
        }
        return Array.from(faces);
    }
    #addFace(faceData, id = this.#getNewId()) {
        const face = new Face(id, faceData, this);
        this.#faces.set(id, face);
        for (const [edge] of face.edges()) {
            let faces = this.#facesByEdge.get(edge.id);
            if (!faces) {
                faces = new Set();
                this.#facesByEdge.set(edge.id, faces);
            }
            faces.add(face);
        }
        return face;
    }
    #removeVertex(vertex) {
        this.#vertices.delete(vertex.id);
        this.#edgesByVertex.delete(vertex.id);
        let oposite = this.#oppositeVerticies.get(vertex.id);
        if (oposite) {
            this.#oppositeVerticies.delete(vertex.id);
            this.#oppositeVerticies.delete(oposite.id);
        }
    }
    #removeEdge(edge) {
        this.#edges.delete(edge.id);
        for (const vertex of edge.vertices) {
            const edges = this.#edgesByVertex.get(vertex.id);
            if (edges) {
                edges.delete(edge);
                if (edges.size === 0) {
                    this.#removeVertex(vertex);
                }
            }
        }
    }
    #removeFace(face) {
        this.#faces.delete(face.id);
        for (const [edge] of face.edges()) {
            const faces = this.#facesByEdge.get(edge.id);
            if (faces) {
                faces.delete(face);
                if (faces.size === 0) {
                    this.#removeEdge(edge);
                }
            }
        }
    }
    #getNewId() {
        let id;
        do {
            id = `${this.#counter++}`;
        } while (this.#vertices.has(id) || this.#edges.has(id) || this.#faces.has(id));
        return id;
    }
    classifyEdges(plane) {
        const adjecent = new Map();
        const tangent = new Set();
        const preserved = new Set();
        const rejected = new Set();
        for (const edge of this.#edges.values()) {
            const result = edge.intersectWithPlane(plane);
            switch (result.type) {
                case "EDGE_TANGENT":
                    tangent.add(edge);
                    break;
                case "EDGE_INSIDE":
                    preserved.add(edge);
                    break;
                case "EDGE_ADJECENT":
                    adjecent.set(edge, result);
                    break;
                case "EDGE_OUTSIDE":
                    rejected.add(edge);
                    break;
            }
        }
        return {
            adjecent,
            tangent,
            preserved,
            rejected,
        };
    }
    intersectWithPlane(a, b, c, d, color) {
        const plane = new THREE.Vector4(a, b, c, d);
        if (this.#faces.size === 0) {
            return this.#addFace({
                edges: {},
                plane: plane.toArray(),
                color,
            });
        }
        const { adjecent, preserved, tangent } = this.classifyEdges(plane);
        const newFaceEdges = new Map();
        function addToBoundary(id, sign) {
            const value = newFaceEdges.get(id) ?? 0;
            if (value + sign === 0) {
                newFaceEdges.delete(id);
            }
            else {
                newFaceEdges.set(id, value + sign);
            }
        }
        const facesToRemove = new Set();
        const faces = Array.from(this.#faces.values());
        for (const face of faces) {
            const line = face.getIntersectionLine(plane);
            if (!line) {
                // If there's no intersection line, then the face is fully contained in the plane.
                if (face.plane.dot(plane) < 0) {
                    facesToRemove.add(face);
                    for (const [edge, sign] of face.edges()) {
                        addToBoundary(edge.id, -sign);
                    }
                }
                continue;
            }
            const edges = new Map();
            if (face.nEdges === 0) {
                const edge = this.addEdge({
                    vertices: [],
                    line: line.toArray(),
                    color: face.color,
                });
                edges.set(edge.id, 1);
                addToBoundary(edge.id, -1);
            }
            const intersections = [];
            for (const [edge, sign] of face.edges()) {
                const intersection = adjecent.get(edge);
                if (intersection) {
                    edges.set(intersection.edge.id, sign);
                    intersections.push({
                        ...intersection,
                        sign: intersection.sign * sign,
                    });
                }
                else if (tangent.has(edge)) {
                    // TODO: This is trikcy, explain.
                    if (sign * edge.dot(line)) {
                        edges.set(edge.id, sign);
                    }
                    else {
                        addToBoundary(edge.id, sign);
                    }
                }
                else if (preserved.has(edge)) {
                    edges.set(edge.id, sign);
                }
            }
            const cmp = line.getPointCompare();
            intersections.sort((a, b) => {
                if (a.vertex.id === b.vertex.id) {
                    return a.sign - b.sign;
                }
                return cmp(a.vertex, b.vertex);
            });
            if (intersections.length > 0) {
                if (intersections[0].sign === 1) {
                    const { vertex } = intersections[0];
                    const newVertex = this.addOppositeVertex(vertex.id);
                    const [newEdge, sign] = this.getOrAddEdge(newVertex.id, vertex.id, line, face.color);
                    edges.set(newEdge.id, sign);
                    addToBoundary(newEdge.id, -sign);
                }
                let previous = intersections[0];
                let i = 1;
                while (i < intersections.length) {
                    const { sign, vertex } = intersections[i];
                    if (previous.sign === -1 && sign === 1 && previous.vertex.id !== vertex.id) {
                        const [newEdge, sign] = this.getOrAddEdge(previous.vertex.id, vertex.id, line, face.color);
                        edges.set(newEdge.id, sign);
                        addToBoundary(newEdge.id, -sign);
                    }
                    previous = intersections[i];
                    i++;
                }
                if (intersections[intersections.length - 1].sign === -1) {
                    const { vertex } = intersections[intersections.length - 1];
                    const newVertex = this.addOppositeVertex(vertex.id);
                    const [newEdge, sign] = this.getOrAddEdge(vertex.id, newVertex.id, line, face.color);
                    edges.set(newEdge.id, sign);
                    addToBoundary(newEdge.id, -sign);
                }
            }
            if (!face.hasTheSameEdges(edges)) {
                facesToRemove.add(face);
                if (edges.size > 0) {
                    this.#addFace({
                        edges: Object.fromEntries(edges.entries()),
                        plane: face.plane.toArray(),
                        color: face.color,
                    });
                }
            }
        }
        let newFace;
        if (newFaceEdges.size > 0) {
            const face = this.findFaceByEdges(newFaceEdges);
            if (face) {
                newFace = face;
            }
            else {
                newFace = this.#addFace({
                    edges: Object.fromEntries(newFaceEdges.entries()),
                    plane: plane.toArray(),
                    color,
                });
            }
        }
        for (const faces of facesToRemove) {
            this.#removeFace(faces);
        }
        return newFace;
    }
    *faces() {
        yield* this.#faces.values();
    }
    *vertices() {
        yield* this.#vertices.values();
    }
    *edges() {
        yield* this.#edges.values();
    }
    get nFaces() {
        return this.#faces.size;
    }
    get nEdges() {
        return this.#edges.size;
    }
    get nVertices() {
        return this.#vertices.size;
    }
    getFacesVector() {
        const faces = new Map();
        for (const face of this.faces()) {
            faces.set(face.id, 1);
        }
        return faces;
    }
    getFacesSortedById() {
        return Array.from(this.#faces.values()).sort((a, b) => {
            return a.id < b.id ? -1 : 1;
        });
    }
    get eulerCharacteristic() {
        return this.nVertices - this.nEdges + this.nFaces;
    }
    getCenter() {
        const center = new THREE.Vector3();
        for (const vertex of this.vertices()) {
            center.add(vertex.toAffine());
        }
        if (this.nVertices > 0) {
            center.divideScalar(this.nVertices);
        }
        return center;
    }
    getBoundary(faces = this.getFacesVector()) {
        const boundary = new Map();
        for (const [faceId, sign] of faces.entries()) {
            const face = this.#faces.get(faceId);
            if (face) {
                for (const [edge, edgeSign] of face.edges()) {
                    const value = (boundary.get(edge.id) ?? 0) + sign * edgeSign;
                    if (value !== 0) {
                        boundary.set(edge.id, value);
                    }
                    else {
                        boundary.delete(edge.id);
                    }
                }
            }
        }
        return boundary;
    }
    // getEdgeGeometry() {
    //   const geometries: GeometryAttributes[] = [];
    //   for (const face of this.faces()) {
    //     geometries.push(face.getEdgeGeometryAttributes());
    //   }
    //   return createEdgeGeometry(combineGeometryAttributes(geometries));
    // }
    getFaceGeometry(options = {}) {
        const geometries = [];
        const faces = this.getFacesSortedById();
        for (let i = 0; i < faces.length; i++) {
            const face = faces[i];
            geometries.push(face.getFaceGeometryAttributes({
                edgeWidth: options.edgeWidth,
                noBevel: options.noBevel,
                skinIndex: i,
            }));
        }
        return createFaceGeometry(combineGeometryAttributes(geometries));
    }
    getBevelGeometry(options = {}) {
        const { edgeWidth = 0.1 } = options;
        const geometries = [];
        const faces = this.getFacesSortedById();
        for (let i = 0; i < faces.length; i++) {
            const face = faces[i];
            geometries.push(face.getBevelGeometryAttributes({ edgeWidth, skinIndex: i }));
        }
        return createFaceGeometry(combineGeometryAttributes(geometries));
    }
    getData() {
        return {
            vertices: Object.fromEntries(Array.from(this.#vertices.entries()).map(([id, vertex]) => [id, vertex.getData()])),
            edges: Object.fromEntries(Array.from(this.#edges.entries()).map(([id, edge]) => [id, edge.getData()])),
            faces: Object.fromEntries(Array.from(this.#faces.entries()).map(([id, face]) => [id, face.getData()])),
        };
    }
    clone() {
        return new Polyhedron(this.getData());
    }
    getSnapshot() {
        return {
            vertices: Object.fromEntries(Array.from(this.#vertices.entries()).map(([id, vertex]) => [id, vertex.toString()])),
            edges: Object.fromEntries(Array.from(this.#edges.entries()).map(([id, edge]) => [id, edge.toString()])),
            faces: Object.fromEntries(Array.from(this.#faces.entries()).map(([id, face]) => [id, face.toString()])),
        };
    }
}
//# sourceMappingURL=Polyhedron.js.map